import { useLayer } from '@statsig/react-bindings';
import { useCallback } from 'react';

import { Statsig } from '../statsig/StatsigProvider';
import { ExperimentLayers } from '../statsig/experiments';
import { AdmissionFlow } from '../types';
import { VideoPlatform } from '../video-sdk';
import {
  useGetVideoPlatformOfAdmittedRoomCallback,
  useVideoPlatformOfAdmittedRoom,
} from '../visitor/waiting-room/admission/useVideoPlatformOfAdmittedRoom';

const [admissionLayerName, { automaticAdmissionIsEnabled }] = ExperimentLayers.Admission;

function getAdmissionFlow(videoPlatform: VideoPlatform | null, isAutomaticAdmissionEnabled: boolean): AdmissionFlow {
  return videoPlatform === VideoPlatform.Zoom && !isAutomaticAdmissionEnabled ? 'manual' : 'auto';
}

export function useIsAutomaticAdmissionEnabled(): boolean {
  return useLayer(admissionLayerName).get<boolean>(automaticAdmissionIsEnabled, false) ?? false;
}

/**
 * The client user reads the admission experiment and relays their admission flow
 * to the provider side as part of their presence updates.
 *
 * Defaults to manual admission for Zoom (Control), auto otherwise.
 */
export function useAdmissionFlow(): AdmissionFlow {
  const isAutomaticAdmissionEnabled = useIsAutomaticAdmissionEnabled();
  const videoPlatformOfAdmittedRoom = useVideoPlatformOfAdmittedRoom();

  return getAdmissionFlow(videoPlatformOfAdmittedRoom, isAutomaticAdmissionEnabled);
}

export function useAdmissionFlowCallback(): () => AdmissionFlow {
  const getVideoPlatformOfAdmittedRoom = useGetVideoPlatformOfAdmittedRoomCallback();

  return useCallback(() => {
    const isAutomaticAdmissionEnabled = Statsig.getLayer(admissionLayerName).get<boolean>(
      automaticAdmissionIsEnabled,
      false,
    );
    return getAdmissionFlow(getVideoPlatformOfAdmittedRoom(), isAutomaticAdmissionEnabled);
  }, [getVideoPlatformOfAdmittedRoom]);
}
